import type { AeroOptimizationJobSummary } from '../api';

export type AeroOptimizationJobStatus = AeroOptimizationJobSummary['status'];

export const filterAeroJobsForStrategy = ({ jobs, strategyId }: { jobs: AeroOptimizationJobSummary[]; strategyId: number | null }) => {
    if (strategyId === null) return [];

    return [...jobs]
        .filter((job) => job.strategyId === strategyId)
        .sort((left, right) => getSortableTime(right.createdAt) - getSortableTime(left.createdAt));
};

export const getAeroJobProgressPercent = (job: AeroOptimizationJobSummary) => {
    if (job.totalCombinations <= 0) return 0;

    return capUnfinishedProgress(job, (getAeroJobExitConfigsScored(job) / job.totalCombinations) * 100);
};

export const getAeroJobSimulationProgressPercent = (job: AeroOptimizationJobSummary) => {
    if (job.totalEntityCombinationSimulations <= 0) return 0;

    return Math.min(
        100,
        Math.max(0, Math.round((job.entityCombinationSimulationsProcessed / job.totalEntityCombinationSimulations) * 100))
    );
};

export const getAeroJobExitConfigsScored = (job: AeroOptimizationJobSummary): number => job.exitConfigsScored;

export const canUseAeroJobForPromotion = (job: AeroOptimizationJobSummary) => job.status === 'completed';

export const summarizeAeroJobEntities = (job: AeroOptimizationJobSummary) => {
    if (job.entitiesDetailed && job.entitiesDetailed.length > 1) {
        return `${job.entitiesDetailed.length} entities`;
    }

    const entitySymbol = job.entitiesDetailed?.[0]?.entitySymbol ?? job.entitySymbol;
    const entityName = job.entitiesDetailed?.[0]?.entityName ?? job.entityName;

    if (entitySymbol) return `${entitySymbol}${entityName && entityName !== entitySymbol ? ` - ${entityName}` : ''}`;

    return `${job.entityType} ${job.entityId}`;
};

export const formatAeroJobTimestamp = (value: string | null) => {
    if (!value) return 'None';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString();
};

const getSortableTime = (value: string | null) => {
    if (!value) return 0;

    const timestamp = new Date(value).getTime();

    return Number.isNaN(timestamp) ? 0 : timestamp;
};

export type AeroJobPhase = AeroOptimizationJobSummary['currentPhase'];

export const AERO_PHASE_STEPS: { key: AeroJobPhase; label: string }[] = [
    { key: 'candidate_materialization', label: 'Compile exit candidates' },
    { key: 'entry_gen', label: 'Generate entries' },
    { key: 'option_bar_materialization', label: 'Materialize option bars' },
    { key: 'simulation', label: 'Simulate exits' },
    { key: 'aggregation', label: 'Aggregate results' },
    { key: 'pit_snapshot_generation', label: 'Generate point-in-time snapshots' },
    { key: 'detail_materialization', label: 'Materialize detail' }
];

export const formatAeroJobPhase = (phase: AeroJobPhase): string => {
    switch (phase) {
        case 'candidate_materialization':
            return 'Compiling exit candidates';
        case 'entry_gen':
            return 'Generating entries';
        case 'option_bar_materialization':
            return 'Materializing option bars';
        case 'simulation':
            return 'Simulating exit combinations';
        case 'aggregation':
            return 'Aggregating results';
        case 'pit_snapshot_generation':
            return 'Generating point-in-time snapshots';
        case 'detail_materialization':
            return 'Materializing details';
        case 'complete':
            return 'Complete';
        default:
            return phase;
    }
};

export const getAeroPhaseIndex = (phase: AeroJobPhase): number => {
    const index = AERO_PHASE_STEPS.findIndex((step) => step.key === phase);

    return phase === 'complete' ? AERO_PHASE_STEPS.length : index;
};

export const getAeroJobWorkUnitProgressPercent = (job: AeroOptimizationJobSummary): number => {
    if (job.totalWorkUnits <= 0) return 0;

    return capUnfinishedProgress(job, (job.completedWorkUnits / job.totalWorkUnits) * 100);
};

const capUnfinishedProgress = (job: AeroOptimizationJobSummary, percent: number): number =>
    Math.min(job.status === 'completed' ? 100 : 99, Math.max(0, Math.round(percent)));
